import { Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { inrFull } from "@/lib/format";
import { cashBreakdown, CashChange } from "./cashChange";

const today = () => new Date().toLocaleDateString("en-CA");

export const allocationSum = (lines) => Number(lines.reduce((t, l) => t + (Number(l.amount) || 0), 0).toFixed(2));

// Split-tender allocation against an existing invoice balance (collect panel). Display only —
// the server re-validates every tender and the total against the balance.
export function CollectAllocation({ lines, setLines, balance, methods, labels, payDate, setPayDate, reason, setReason, canBackdate }) {
  const cash = cashBreakdown(lines, balance);
  const sum = cash.applied;
  const remaining = Number((balance - sum).toFixed(2));
  const update = (i, patch) => setLines(lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const add = () => setLines([...lines, { method: methods.find((m) => !lines.some((l) => l.method === m)) || methods[0], amount: remaining > 0 ? remaining : "" }]);
  const remove = (i) => setLines(lines.filter((_, idx) => idx !== i));
  const backdated = payDate && payDate < today();
  return (
    <div className="mt-3 space-y-2" data-testid="pos-collect-lines">
      {lines.map((l, i) => (
        <div key={i} className="flex items-center gap-2" data-testid={`pos-collect-line-${i}`}>
          <Select value={l.method} onValueChange={(v) => update(i, { method: v })}>
            <SelectTrigger className="h-9 w-32 bg-white" data-testid={`pos-collect-method-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{methods.map((m) => <SelectItem key={m} value={m}>{labels[m] || m}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="number" min="0" value={l.amount} placeholder={l.method === "cash" ? "Cash received" : "0"} aria-label={l.method === "cash" ? "Cash received" : "Amount"} onChange={(e) => update(i, { amount: e.target.value })}
            className="h-9 w-36 text-right bg-white" data-testid={`pos-collect-amount-${i}`} />
          <button type="button" onClick={() => remove(i)} disabled={lines.length <= 1} aria-label="Remove payment line"
            className="p-1 text-rose-500 hover:text-rose-700 disabled:opacity-30" data-testid={`pos-collect-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <button type="button" onClick={add} className="font-medium text-emerald-700 hover:underline" data-testid="pos-collect-add">+ Split across another method</button>
        <span className="text-slate-500" data-testid="pos-collect-allocated">Allocated {inrFull(sum)} of {inrFull(balance)}</span>
        <span data-testid="pos-collect-remaining" className={remaining < -0.009 ? "text-rose-600 font-semibold" : remaining > 0.009 ? "text-amber-600" : "text-emerald-600"}>
          {remaining < -0.009 ? `Over by ${inrFull(-remaining)}` : remaining > 0.009 ? `Remaining ${inrFull(remaining)}` : "Fully allocated"}
        </span>
      </div>
      <CashChange cash={cash} total={balance} billLabel="Amount Due" prefix="pos-collect-cash" />
      <div className="flex flex-wrap items-center gap-2">
        <Label className="text-xs text-slate-500">Paid on</Label>
        <Input type="date" value={payDate} max={today()} min={canBackdate ? undefined : today()}
          onChange={(e) => setPayDate(e.target.value)} className="h-9 w-40 bg-white" data-testid="pos-collect-payment-date" />
        {backdated && (
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for backdating *"
            className="h-9 flex-1 min-w-[180px] bg-white" data-testid="pos-collect-backdate-reason" />
        )}
      </div>
    </div>
  );
}
